import { useState, useRef, useEffect } from 'react';
import { useChartStore } from '@/store/chartStore';
import type { Timeframe } from '@/types';
import { trackTimeframeChange } from '@/services/gtag';
import { ClockIcon } from '@/components/icons';
import { HeaderPopover } from '@/components/Header/HeaderPopover';

interface TFOption {
  label: string;       // short (buttons)
  long: string;        // long (dropdown)
  value: Timeframe;
}

const INTRADAY_TIMEFRAMES: TFOption[] = [
  { label: '1m',  long: '1 Minute',   value: '1m'  },
  { label: '5m',  long: '5 Minutes',  value: '5m'  },
  { label: '15m', long: '15 Minutes', value: '15m' },
  { label: '30m', long: '30 Minutes', value: '30m' },
  { label: '1h',  long: '1 Hour',     value: '1h'  },
];

const DAILY_TIMEFRAMES: TFOption[] = [
  { label: '1D', long: '1 Day',   value: '1D' },
  { label: '1W', long: '1 Week',  value: '1W' },
  { label: '1M', long: '1 Month', value: '1M' },
];

const ALL_TIMEFRAMES = [...INTRADAY_TIMEFRAMES, ...DAILY_TIMEFRAMES];

export function TimeframeBar() {
  const timeframe    = useChartStore((s) => s.timeframe);
  const setTimeframe = useChartStore((s) => s.setTimeframe);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside pointer-down
  useEffect(() => {
    if (!dropdownOpen) return;
    function handler(e: PointerEvent) {
      if (
        buttonRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      ) return;
      setDropdownOpen(false);
    }
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [dropdownOpen]);

  function handleSelect(tf: Timeframe) {
    setTimeframe(tf);
    trackTimeframeChange(tf);
    setDropdownOpen(false);
  }

  const activeOption = ALL_TIMEFRAMES.find((tf) => tf.value === timeframe);

  return (
    <div className="flex items-center gap-1">
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setDropdownOpen((v) => !v)}
          title={`Timeframe: ${activeOption?.long ?? timeframe}`}
          className={`flex items-center gap-1.5 px-2 py-1 rounded text-base transition-colors ${
            dropdownOpen
              ? 'bg-accent/20 text-accent'
              : 'text-muted hover:text-text hover:bg-panel-hover'
          }`}
        >
          <ClockIcon size={20} />
          <span className="text-sm font-semibold">{activeOption?.label ?? timeframe}</span>
        </button>

        <HeaderPopover
          ref={dropdownRef}
          anchorRef={buttonRef}
          open={dropdownOpen}
          align="left"
          className="mt-1 glass-modal rounded-xl z-dropdown py-1.5 min-w-[160px] animate-menu-enter"
        >
          <p className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-modal-muted">Intraday</p>
          {INTRADAY_TIMEFRAMES.map((tf) => (
            <button
              key={tf.value}
              onClick={() => handleSelect(tf.value)}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center justify-between gap-4 ${
                timeframe === tf.value
                  ? 'text-accent bg-accent/10'
                  : 'text-modal-text hover:bg-modal-hover'
              }`}
            >
              <span>{tf.long}</span>
              <span className="text-xs text-modal-muted font-mono">{tf.label}</span>
            </button>
          ))}
          <div className="h-px bg-border mx-2 my-1" />
          <p className="px-3 pt-0.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-modal-muted">Daily</p>
          {DAILY_TIMEFRAMES.map((tf) => (
            <button
              key={tf.value}
              onClick={() => handleSelect(tf.value)}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center justify-between gap-4 ${
                timeframe === tf.value
                  ? 'text-accent bg-accent/10'
                  : 'text-modal-text hover:bg-modal-hover'
              }`}
            >
              <span>{tf.long}</span>
              <span className="text-xs text-modal-muted font-mono">{tf.label}</span>
            </button>
          ))}
        </HeaderPopover>
      </div>
    </div>
  );
}
